import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ArrowLeft, AlertCircle, Bot } from "lucide-react";
import { ProjectKanbanBoard } from "@/components/project-tracking/ProjectKanbanBoard";
import { ProjectGanttChart } from "@/components/project-tracking/ProjectGanttChart";
import { ProjectPRDPanel } from "@/components/project-tracking/ProjectPRDPanel";
import { PhaseForm } from "@/components/project-tracking/PhaseForm";
import { ProjectShareDialog } from "@/components/projects/ProjectShareDialog";
import { ProjectAIAssistant } from "@/components/project-tracking/ProjectAIAssistant";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { GitMetricsCard } from "@/components/projects/GitMetricsCard";
import { CommitsList } from "@/components/projects/CommitsList";

const ProjectTracking = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { toast } = useToast();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { session, userRole, isLoading: authLoading } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [phases, setPhases] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [gitMetrics, setGitMetrics] = useState<any[]>([]);
  const [gitCommits, setGitCommits] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingGit, setIsLoadingGit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("kanban");
  
  // Refs for stable references and caching
  const phaseChannelRef = useRef<any>(null);
  const taskChannelRef = useRef<any>(null);
  const isTabVisible = useRef(true);
  const dataCache = useRef<{ [key: string]: any }>({});
  const lastFetchTime = useRef<{ [key: string]: number }>({});
  const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache
  const isInitialized = useRef(false);
  
  // Memoized values
  const canEdit = useMemo(() => userRole === 'salesman', [userRole]);
  const hasNoData = useMemo(() => (!phases || phases.length === 0) && (!tasks || tasks.length === 0), [phases, tasks]);
  const isInitialSetup = useMemo(() => hasNoData && !project?.prd, [hasNoData, project?.prd]);

  // Track tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      isTabVisible.current = !document.hidden;
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Optimized data fetching with caching
  const fetchProjectData = useCallback(async (forceRefresh = false) => {
    // Skip if tab is not visible and not forcing refresh
    if (!isTabVisible.current && !forceRefresh) {
      console.log('⏸️ ProjectTracking: Skipping fetch - tab not visible');
      return;
    }

    if (!projectId || !currentOrganization || !session || authLoading) return;

    const cacheKey = `project-${projectId}-${currentOrganization.id}`;
    const now = Date.now();
    
    // Check cache first (unless forcing refresh)
    if (!forceRefresh && dataCache.current[cacheKey] && lastFetchTime.current[cacheKey]) {
      const timeSinceLastFetch = now - lastFetchTime.current[cacheKey];
      if (timeSinceLastFetch < CACHE_DURATION) {
        console.log('📦 ProjectTracking: Using cached data');
        const cachedData = dataCache.current[cacheKey];
        setProject(cachedData.project);
        setPhases(cachedData.phases || []);
        setTasks(cachedData.tasks || []);
        setIsLoading(false);
        return;
      }
    }

    setError(null);
    setIsLoading(true);
    
    try {
      const { data, error: edgeFunctionError } = await supabase.functions.invoke('project-operations', {
        body: {
          action: "get",
          organizationId: currentOrganization.id,
        },
      });

      if (edgeFunctionError) throw edgeFunctionError;
      
      if (!data || !data.projects) throw new Error("No projects data received");
      
      const projectData = data.projects.find((p: any) => p.id === projectId);
      
      if (!projectData) {
        setError("Project not found");
        setIsLoading(false);
        return;
      }
      
      setProject(projectData);

      console.log("Fetching tracking data for project:", projectId);
      const { data: trackingData, error: trackingError } = await supabase.functions.invoke('project-tracking-operations', {
        body: {
          action: "getTrackingData",
          projectId,
          organizationId: currentOrganization.id,
          userId: session.user.id
        },
      });

      console.log("Tracking data response:", trackingData);
      
      if (trackingError) {
        console.error("Tracking data error:", trackingError);
        throw trackingError;
      }
      
      if (!trackingData || !trackingData.success) {
        throw new Error(trackingData?.error || "Failed to fetch tracking data");
      }
      
      const phases = trackingData.phases || [];
      const tasks = trackingData.tasks || [];
      
      setPhases(phases);
      setTasks(tasks);
      
      // Cache the data
      dataCache.current[cacheKey] = {
        project: projectData,
        phases,
        tasks
      };
      lastFetchTime.current[cacheKey] = now;
      
      console.log("Phases set:", phases);
      console.log("Tasks set:", tasks);

      // Fetch Git metrics if project has git integration
      if (projectData.repository_url && projectData.github_owner && projectData.repository_name) {
        await fetchGitMetrics(projectData);
      }
    } catch (error: any) {
      console.error("Error fetching project data:", error);
      setError(error.message || "Failed to load project data");
      toast({
        title: "Error",
        description: "Failed to load project data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, currentOrganization, session, authLoading, toast]);

  // Main data loading effect with debouncing
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    const timeoutId = setTimeout(() => {
      fetchProjectData();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [fetchProjectData]);

  // Optimized real-time channel management  
  useEffect(() => {
    if (!projectId || !currentOrganization || !session) return;
    
    // Clean up existing channels
    if (phaseChannelRef.current) {
      supabase.removeChannel(phaseChannelRef.current);
    }
    if (taskChannelRef.current) {
      supabase.removeChannel(taskChannelRef.current);
    }
    
    // Create new channels with refs
    phaseChannelRef.current = supabase
      .channel(`project-phases-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_phases',
          filter: `project_id=eq.${projectId}`
        },
        () => {
          // Only refresh if tab is visible
          if (isTabVisible.current) {
            refreshTrackingData();
          }
        }
      )
      .subscribe();

    taskChannelRef.current = supabase
      .channel(`project-tasks-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_tasks',
          filter: `project_id=eq.${projectId}`
        },
        () => {
          // Only refresh if tab is visible
          if (isTabVisible.current) {
            refreshTrackingData();
          }
        }
      )
      .subscribe();

    return () => {
      if (phaseChannelRef.current) {
        supabase.removeChannel(phaseChannelRef.current);
        phaseChannelRef.current = null;
      }
      if (taskChannelRef.current) {
        supabase.removeChannel(taskChannelRef.current);
        taskChannelRef.current = null;
      }
    };
  }, [projectId, currentOrganization, session]);

  const fetchGitMetrics = useCallback(async (projectData: any) => {
    if (!projectData.repository_url || !projectData.github_owner || !projectData.repository_name) return;
    
    setIsLoadingGit(true);
    try {
      const { data, error } = await supabase.functions.invoke('github-integration', {
        body: {
          action: "getMetrics",
          projectId: projectData.id,
          organizationId: currentOrganization?.id,
        },
      });

      if (error) {
        console.error("Error fetching git metrics:", error);
        return;
      }
      
      if (data && data.success) {
        setGitMetrics(data.metrics || []);
        setGitCommits(data.commits || []);
      }
    } catch (error) {
      console.error("Error fetching git metrics:", error);
    } finally {
      setIsLoadingGit(false);
    }
  }, [currentOrganization?.id]);

  const refreshTrackingData = useCallback(async () => {
    if (!projectId || !currentOrganization || !session) return;
    
    // Skip if tab is not visible
    if (!isTabVisible.current) {
      console.log('⏸️ ProjectTracking: Skipping refresh - tab not visible');
      return;
    }
    
    try {
      console.log("Refreshing tracking data...");
      const { data, error } = await supabase.functions.invoke('project-tracking-operations', {
        body: {
          action: "getTrackingData",
          projectId,
          organizationId: currentOrganization.id,
          userId: session.user.id
        },
      });

      if (error) {
        console.error("Error refreshing tracking data:", error);
        throw error;
      }
      
      if (data && data.success) {
        console.log("Refreshed phases:", data.phases);
        console.log("Refreshed tasks:", data.tasks);
        setPhases(data.phases || []);
        setTasks(data.tasks || []);
        
        // Update cache
        const cacheKey = `project-${projectId}-${currentOrganization.id}`;
        if (dataCache.current[cacheKey]) {
          dataCache.current[cacheKey] = {
            ...dataCache.current[cacheKey],
            phases: data.phases || [],
            tasks: data.tasks || []
          };
          lastFetchTime.current[cacheKey] = Date.now();
        }
        
        // Show success toast for data refresh
        toast({
          title: "Data Updated",
          description: "Project data has been refreshed successfully",
        });
      }
    } catch (error) {
      console.error("Error refreshing tracking data:", error);
      toast({
        title: "Refresh Error",
        description: "Failed to refresh project data",
        variant: "destructive",
      });
    }
  }, [projectId, currentOrganization, session, toast]);

  if (orgLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto py-6">
          <BreadcrumbNav />
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold mb-4">Project Not Found</h1>
            <p className="text-muted-foreground mb-6">The project you're looking for doesn't exist or you don't have access to it.</p>
            {error && (
              <Alert variant="destructive" className="mb-6 mx-auto max-w-md">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  Failed to load project data
                </AlertDescription>
              </Alert>
            )}
            <Button onClick={() => navigate("/project-dashboard")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Projects
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/' },
    { label: 'Projects', href: '/project-dashboard' },
    { label: project?.name || 'Project', href: `/projects/${projectId}` },
    { label: 'Tracking' }
  ];

  const triggerAIAssistant = () => {
    const aiButton = document.querySelector('[data-ai-assistant-trigger]') as HTMLButtonElement;
    if (aiButton) {
      aiButton.click();
    } else {
      console.log('AI Assistant button not found');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto py-6">
        <BreadcrumbNav items={breadcrumbItems} />
        
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate("/project-dashboard")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{project?.name}</h1>
              <p className="text-muted-foreground">{project?.description}</p>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            {project && (
              <ProjectShareDialog 
                projectId={project.id} 
                projectName={project.name} 
              />
            )}
            {canEdit && project?.prd && phases?.length > 0 && (
              <Button 
                onClick={triggerAIAssistant}
                size="sm"
                className="bg-primary text-white hover:bg-primary/90"
              >
                <Bot className="h-4 w-4 mr-2" />
                Generate AI Tasks
              </Button>
            )}
          </div>
        </div>
        
        <Tabs 
          defaultValue={isInitialSetup ? "prd" : "kanban"} 
          value={activeTab} 
          onValueChange={setActiveTab} 
          className="w-full"
        >
          <TabsList className="mb-4">
            {canEdit && (
              <TabsTrigger value="prd">PRD / Phases</TabsTrigger>
            )}
            <TabsTrigger value="kanban">Kanban Board</TabsTrigger>
            <TabsTrigger value="gantt">Gantt Chart</TabsTrigger>
          </TabsList>
          
          {canEdit && (
            <TabsContent value="prd" className="mt-0">
              <ProjectPRDPanel 
                project={project}
                phases={phases}
                tasks={tasks}
                isInitialSetup={isInitialSetup}
                onDataChange={refreshTrackingData}
              />
            </TabsContent>
          )}
          
          <TabsContent value="kanban" className="mt-0">
            {hasNoData && canEdit ? (
              <div className="text-center py-12 space-y-4">
                <div className="text-muted-foreground">
                  <h3 className="text-lg font-medium mb-2">Project Setup Required</h3>
                  <p>Please complete the project setup to start working with phases and tasks.</p>
                </div>
                <Button onClick={() => setActiveTab("prd")}>
                  Complete Setup
                </Button>
              </div>
            ) : (
              <ProjectKanbanBoard 
                projectId={projectId || ''}
                phases={phases}
                tasks={tasks}
                onTasksChange={setTasks}
                onPhasesChange={setPhases}
                userRole={userRole}
              />
            )}
          </TabsContent>
          
          <TabsContent value="gantt" className="mt-0">
            <ProjectGanttChart 
              projectId={projectId || ''}
              phases={phases}
              tasks={tasks}
              onTasksChange={setTasks}
              onPhasesChange={setPhases}
              userRole={userRole}
            />
          </TabsContent>
        </Tabs>

        {/* Git Metrics Section */}
        {project?.repository_url && project?.github_owner && project?.repository_name && (
          <div className="mt-8 space-y-6">
            <h2 className="text-xl font-semibold">Git Activity</h2>
            {isLoadingGit ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Loading Git metrics...</span>
              </div>
            ) : (
              <div className="grid gap-6">
                <GitMetricsCard 
                  metrics={gitMetrics}
                  repositoryUrl={project.repository_url}
                  projectId={project.id}
                  organizationId={currentOrganization?.id}
                  githubOwner={project.github_owner}
                  repositoryName={project.repository_name}
                  onRefresh={() => fetchGitMetrics(project)}
                  className="w-full"
                />
                <CommitsList 
                  commits={gitCommits}
                  isLoading={isLoadingGit}
                  onRefresh={() => fetchGitMetrics(project)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {/* AI Assistant */}
        <ProjectAIAssistant 
          projectId={projectId || ''}
          project={project}
          phases={phases}
          tasks={tasks}
          onDataChange={refreshTrackingData}
        />
      </div>
    </div>
  );
};

export default ProjectTracking;
